"use client";

import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import BrandLogo from "@/assets/logo.svg";

// Module-level flag survives React remounts (e.g. WagmiProvider key change)
// but resets on full page reload, so splash only plays once per load.
let hasShownSplash = false;

export const SplashScreen = () => {
  const tc = useTranslations("common");
  const [phase, setPhase] = useState<"enter" | "exit" | "done">("enter");

  // useLayoutEffect fires before paint — prevents content flash on remount.
  // If splash already shown, jump straight to "done" before first paint.
  useLayoutEffect(() => {
    if (hasShownSplash) {
      setPhase("done");
    } else {
      hasShownSplash = true;
    }
  }, []);

  useEffect(() => {
    if (phase !== "enter") return;
    const timer = setTimeout(() => {
      setPhase("exit");
    }, 1800);
    return () => clearTimeout(timer);
  }, [phase]);

  const handleAnimationEnd = useCallback(() => {
    if (phase === "exit") {
      setPhase("done");
    }
  }, [phase]);

  if (phase === "done") return null;

  return (
    <div
      suppressHydrationWarning
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-background"
      style={
        phase === "exit"
          ? { animation: "splash-exit 0.4s ease-in-out forwards" }
          : undefined
      }
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        className="flex flex-col items-center gap-4"
        style={{ animation: "splash-enter 0.5s ease-out both" }}
      >
        <BrandLogo
          width={56}
          height={56}
          aria-label={tc("brand")}
          className="text-accent"
        />
        <span className="text-xl font-bold text-text-primary">
          {tc("brand")}
        </span>
      </div>
    </div>
  );
};
